import { useLayoutEffect, type RefObject } from 'react';

/** The Lite app header's height, for anything pinned beneath it. */
export const LITE_HEADER_HEIGHT_VAR = '--lite-header-h';

/**
 * Publishes an element's height as a CSS variable on the document root and
 * keeps it current as the element resizes, so a sibling can stick directly
 * under it (`top: var(--name, fallback)`) whatever its height turns out to be.
 */
export function usePublishedHeight(
  ref: RefObject<HTMLElement | null>,
  cssVar: string
): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement;
    const publish = () =>
      root.style.setProperty(cssVar, `${el.offsetHeight}px`);
    publish();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(publish);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(cssVar);
    };
  }, [ref, cssVar]);
}
